import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import MaturaFunctionGraph from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import MaturaPiecewiseInequalityExplanationGraph from "./MaturaPiecewiseInequalityExplanationGraph";

import IntervalInput from "../../../../components/inputs/IntervalInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

import {
  eq,
  randInt,
  parseNumberFromAny,
  getFractionRaw,
  getIntervalRaw,
  parseSingleInterval,
  formatLinear,
  intervalObjectToLatex,
  ineqSignToLatex,
  tryGenerateV,
  flipToHat,
} from "./helpers/MaturaPiecewiseFunctionUtils";

// Monotoniczność: bierzemy przedział kawałka,
// ale jeśli punkt łączenia (kink) NALEŻY do dziedziny (kinkExists),
// to domykamy w nim przedział monotoniczności.
function monoIntervalFromPiece(pieceInterval, kinkX, kinkExists) {
  if (!pieceInterval) return null;
  const J = { ...pieceInterval };

  if (Number.isFinite(kinkX) && kinkExists) {
    if (Math.abs(J.left - kinkX) < 1e-9) J.leftClosed = true;
    if (Math.abs(J.right - kinkX) < 1e-9) J.rightClosed = true;
  }

  return J;
}



/* ========================================================================== */
/* GENERATOR ZADANIA                                                          */
/* ========================================================================== */

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 12 (12.1 + 12.2):
 *   f(x) = { -2x - 10  dla x ∈ (-5, -3];  x - 1  dla x ∈ (-3, 4] }  (kształt „V”, h=-3, v=-4)
 *   1) miejsce zerowe: 1
 *   2) f(-2) + 3 f(2) = -3 + 3·1 = 0
 *   3) zbiór wartości: [-4, 3]
 *   4) f(x) < -2  →  (-4, -1)
 */
function createOriginalTask() {
  const interval1 = { left: -5, right: -3, leftClosed: false, rightClosed: true };
  const interval2 = { left: -3, right: 4, leftClosed: false, rightClosed: true };

  // Punkty wykresu (jak w generatorze „V”): A/C – końce z markerami, AL/B/CR – linia łamana.
  const points = {
    A: { x: -5, y: 0, markerSymbol: "circle-open", showLabel: false },
    AL: { x: -5, y: 0, showDot: false, showMarker: false, showLabel: false, label: "" },
    B: { x: -3, y: -4, showDot: false, showMarker: false, showLabel: false },
    CR: { x: 4, y: 3, showDot: false, showMarker: false, showLabel: false, label: "" },
    C: { x: 4, y: 3, markerSymbol: undefined, showLabel: false },
  };

  return {
    type: "MaturaPiecewiseFunctionOpenTask",
    data: {
      shape: "V",
      a1: -2,
      b1: -10,
      interval1,
      a2: 1,
      b2: -1,
      interval2,
      points,
      zeroX: 1,
      exprX1: -2,
      exprX2: 2,
      k: 3,
      exprValue: 0,
      range: { left: -4, right: 3, leftClosed: true, rightClosed: true },
      ineqSign: "<",
      ineqThreshold: -2,
      ineqSolution: { left: -4, right: -1, leftClosed: false, rightClosed: false },
      incInterval: { left: -3, right: 4, leftClosed: true, rightClosed: true },
      decInterval: { left: -5, right: -3, leftClosed: false, rightClosed: true },
      slot3Type: "range",
      kinkX: -3,
      kinkExists: true,
      maxPoints: 4,
      h: -3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 12,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let baseV = null;
  for (let i = 0; i < 200; i++) {
    baseV = tryGenerateV();
    if (baseV) break;
  }
  if (!baseV) baseV = tryGenerateV();

  // losowo V lub ^ (po odbiciu)
  const dataBase = Math.random() < 0.5 ? flipToHat(baseV) : baseV;

  const {
    a1,
    a2,
    interval1,
    interval2,
    points,
    range,
    ineqSign,
    ineqThreshold,
    ineqSolution,
  } = dataBase;

  // przedziały monotoniczności: zależą od znaku współczynników,
  // przedziały monotoniczności: bierzemy przedział kawałka,
  // ale w punkcie łączenia (kink) domykamy, jeśli funkcja w kink istnieje (kinkExists).

  let incInterval = null;
  let decInterval = null;

  const kinkX = Number.isFinite(dataBase.h) ? dataBase.h : interval2.left;
  const kinkExists =
  (Number.isFinite(kinkX) && eq(interval1.right, kinkX) && !!interval1.rightClosed) ||
  (Number.isFinite(kinkX) && eq(interval2.left, kinkX) && !!interval2.leftClosed);




  if (a1 > 0) incInterval = monoIntervalFromPiece(interval1, kinkX, kinkExists);
  if (a1 < 0) decInterval = monoIntervalFromPiece(interval1, kinkX, kinkExists);

  if (a2 > 0) incInterval = monoIntervalFromPiece(interval2, kinkX, kinkExists);
  if (a2 < 0) decInterval = monoIntervalFromPiece(interval2, kinkX, kinkExists);



  // slot 3: losujemy z: zbiór wartości / rośnie / maleje
  const slot3Options = ["range"];
  if (incInterval) slot3Options.push("inc");
  if (decInterval) slot3Options.push("dec");

  const slot3Type =
    slot3Options[randInt(0, slot3Options.length - 1)];

  return {
    type: "MaturaPiecewiseFunctionOpenTask",
    data: {
      ...dataBase,
      maxPoints: 4,
      range,
      ineqSign,
      ineqThreshold,
      ineqSolution,
      incInterval,
      decInterval,
      slot3Type, // "range" | "inc" | "dec"
      kinkX,
      kinkExists,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_12" },
  };
}

/* ========================================================================== */
/* TREŚĆ ZADANIA                                                              */
/* ========================================================================== */

export function renderTask({ data }) {
  const {
    a1,
    b1,
    a2,
    b2,
    interval1,
    interval2,
    points,
    exprX1,
    exprX2,
    k,
    ineqSign,
    ineqThreshold,
    slot3Type,
  } = data;

  const piece1 = formatLinear(a1, b1);
  const piece2 = formatLinear(a2, b2);

  const interval1Latex = `${
    interval1.leftClosed ? "\\left[" : "\\left("
  }${interval1.left},\\, ${interval1.right}${
    interval1.rightClosed ? "\\right]" : "\\right)"
  }`;

  const interval2Latex = `${
    interval2.leftClosed ? "\\left[" : "\\left("
  }${interval2.left},\\, ${interval2.right}${
    interval2.rightClosed ? "\\right]" : "\\right)"
  }`;

  const sizePx = 350;

  const slot3Content =
    slot3Type === "inc" ? (
      <>
        Maksymalnym przedziałem, w którym funkcja <InlineMath math={"f"} /> rośnie,
        jest
      </>
    ) : slot3Type === "dec" ? (
      <>
        Maksymalnym przedziałem, w którym funkcja <InlineMath math={"f"} /> maleje,
        jest
      </>
    ) : (
      <>
        Zbiorem wartości funkcji <InlineMath math={"f"} /> jest przedział
      </>
    );


  return (
    <div className="flex flex-col gap-3">
      <p>
        Funkcja <InlineMath math={"f"} /> jest określona następująco:
      </p>

      <BlockMath
        math={`f(x)=
\\begin{cases}
${piece1} & \\text{dla } x \\in ${interval1Latex},\\\\[4pt]
${piece2} & \\text{dla } x \\in ${interval2Latex}.
\\end{cases}`}
      />

      <div
        style={{
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          maxWidth: "100%",
          margin: "10px auto 20px",
        }}
      >
        <MaturaFunctionGraph
          width={sizePx}
          points={points}
          segments={[
            { p1: "AL", p2: "B" },
            { p1: "B", p2: "CR" },
          ]}
          xRange={[-6, 6]}
          yRange={[-6, 6]}
        />
      </div>

      <p>Uzupełnij odpowiedzi:</p>

      <ol style={{ paddingLeft: 20 }}>
        <li>
          Miejscem zerowym funkcji <InlineMath math={"f"} /> jest liczba{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Wartość wyrażenia{" "}
          <InlineMath math={`f(${exprX1})+${k}f(${exprX2})`} /> jest równa{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          {slot3Content} <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Zbiorem wszystkich rozwiązań nierówności{" "}
          <InlineMath
            math={`f(x) ${ineqSignToLatex(
              ineqSign
            )} ${ineqThreshold}`}
          />{" "}
          jest przedział <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
      </ol>

      
    </div>
  );
}

/* ========================================================================== */
/* INPUT UŻYTKOWNIKA                                                          */
/* ========================================================================== */

export function renderInput(task, userInput = {}, onChange, disabled) {
  const { data } = task;
  const { exprX1, exprX2, k, ineqSign, ineqThreshold, slot3Type } = data;

  const slot3Label =
    slot3Type === "inc"
      ? "Maksymalny przedział, w którym funkcja f rośnie:"
      : slot3Type === "dec"
      ? "Maksymalny przedział, w którym funkcja f maleje:"
      : "Zbiorem wartości funkcji f jest przedział:";

  return (
    <div className="flex flex-col gap-4">
      <OpenTaskScoringHint variant="independent" />
      {/* Slot 1 – miejsce zerowe */}
      <div className="flex flex-col gap-1">
        <div>
          <strong>1.</strong> Miejscem zerowym funkcji jest liczba:
        </div>
        <div className="max-w-[260px]">
          <FractionInput
            value={userInput.x0}
            onChange={(v) => onChange({ ...userInput, x0: v })}
            disabled={disabled}
            placeholder="np. 1"
            label="x_0"
          />
        </div>
      </div>

      {/* Slot 2 – f(a) + k f(b) */}
      <div className="flex flex-col gap-1">
        <div>
          <strong>2.</strong> Wartość wyrażenia{" "}
          <InlineMath math={`f(${exprX1})+${k}f(${exprX2})`} /> jest równa:
        </div>
        <div className="max-w-[260px]">
          <FractionInput
            value={userInput.E}
            onChange={(v) => onChange({ ...userInput, E: v })}
            disabled={disabled}
            placeholder="np. 0"
            label=""
          />
        </div>
      </div>


      {/* Slot 3 – globalna własność */}
      <div className="flex flex-col gap-1">
        <div>
          <strong>3.</strong> {slot3Label}
        </div>
        <IntervalInput
          value={userInput.R}
          onChange={(v) => onChange({ ...userInput, R: v })}
          asString
          disabled={disabled}
          previewWithX={false}
          placeholder="np. [-3,2]"
        />
      </div>

      {/* Slot 4 – nierówność */}
      <div className="flex flex-col gap-1">
        <div>
          <strong>4.</strong> Zbiorem wszystkich rozwiązań nierówności{" "}
          <InlineMath
            math={`f(x) ${ineqSignToLatex(
              ineqSign
            )} ${ineqThreshold}`}
          />{" "}
          jest przedział:
        </div>
        <IntervalInput
          value={userInput.S}
          onChange={(v) => onChange({ ...userInput, S: v })}
          asString
          disabled={disabled}
          previewWithX={false}
          placeholder="np. (-1,2]"
        />
      </div>
    </div>
  );
}

/* ========================================================================== */
/* SPRAWDZANIE ODPOWIEDZI                                                     */
/* ========================================================================== */

export function checkAnswer(task, userInput = {}) {
  const { data } = task;
  const {
    zeroX,
    exprValue,
    range,
    ineqSolution,
    maxPoints,
    slot3Type,
    incInterval,
    decInterval,
  } = data;

  let score = 0;
  const parts = {};

  // 1. Miejsce zerowe
  const x0 = parseNumberFromAny(userInput.x0);
  const ok1 = Number.isFinite(x0) && eq(x0, zeroX);
  if (ok1) score++;
  parts.x0 = ok1;

  // 2. Wyrażenie
  const ev = parseNumberFromAny(userInput.E);
  const ok2 = Number.isFinite(ev) && eq(ev, exprValue);
  if (ok2) score++;
  parts.expr = ok2;

  // 3. Własność globalna
  const rawR = getIntervalRaw(userInput, "R");
  const parsedR = rawR && rawR.trim() ? parseSingleInterval(rawR) : null;

  let targetInterval = null;
  if (slot3Type === "inc") targetInterval = incInterval;
  else if (slot3Type === "dec") targetInterval = decInterval;
  else targetInterval = range;

  let ok3 = false;
  if (parsedR && targetInterval) {
    ok3 =
      eq(parsedR.left, targetInterval.left) &&
      eq(parsedR.right, targetInterval.right) &&
      !!parsedR.leftClosed === !!targetInterval.leftClosed &&
      !!parsedR.rightClosed === !!targetInterval.rightClosed;
  }


  if (ok3) score++;
  parts.slot3 = ok3;

  // 4. Nierówność
  const rawS = getIntervalRaw(userInput, "S");
  const parsedS = rawS && rawS.trim() ? parseSingleInterval(rawS) : null;

  let ok4 = false;
  if (parsedS && ineqSolution) {
    ok4 =
      eq(parsedS.left, ineqSolution.left) &&
      eq(parsedS.right, ineqSolution.right) &&
      !!parsedS.leftClosed === !!ineqSolution.leftClosed &&
      !!parsedS.rightClosed === !!ineqSolution.rightClosed;
  }
  if (ok4) score++;
  parts.ineq = ok4;

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts,
  };
}

/* ========================================================================== */
/* POPRAWNA ODPOWIEDŹ                                                         */
/* ========================================================================== */

export function renderAnswer({ data }) {
  const {
    zeroX,
    exprX1,
    exprX2,
    k,
    exprValue,
    range,
    ineqSolution,
    ineqSign,
    ineqThreshold,
    slot3Type,
    incInterval,
    decInterval,
  } = data;

  const slot3Interval =
    slot3Type === "inc"
      ? incInterval
      : slot3Type === "dec"
      ? decInterval
      : range;

  const slot3Latex = intervalObjectToLatex(slot3Interval);
  const ineqLatex = intervalObjectToLatex(ineqSolution);

  const slot3TextPrefix =
    slot3Type === "inc"
      ? <>Maksymalnym przedziałem, w którym funkcja <InlineMath math="f" /> rośnie, jest </>
      : slot3Type === "dec"
      ? <>Maksymalnym przedziałem, w którym funkcja <InlineMath math="f" /> maleje, jest </>
      : <>Zbiorem wartości funkcji <InlineMath math="f" /> jest przedział </>;

  return (
    <div className="flex flex-col gap-2" style={{ textAlign: "left" }}>
      <div>
        <strong>1.</strong>{" "}
        <InlineMath math={`x_0 = ${zeroX}`} />
      </div>

      <div>
        <strong>2.</strong>{" "}
        <InlineMath
          math={`f(${exprX1})+${k}f(${exprX2}) = ${exprValue}`}
        />
      </div>

      <div>
        <strong>3.</strong> {slot3TextPrefix}
        <InlineMath math={slot3Latex} />.
      </div>

      <div>
        <strong>4.</strong> Zbiorem wszystkich rozwiązań nierówności{" "}
        <InlineMath
          math={`f(x) ${ineqSignToLatex(
            ineqSign
          )} ${ineqThreshold}`}
        />{" "}
        jest przedział <InlineMath math={ineqLatex} />.
      </div>
    </div>
  );
}

/* ========================================================================== */
/* WYJAŚNIENIE (skrótowe)                                                     */
/* ========================================================================== */

export function renderExplanation({ data }) {
  const {
    a1,
    b1,
    a2,
    b2,
    interval1,
    interval2,
    points,

    zeroX,

    exprX1,
    exprX2,
    k,
    exprValue,

    slot3Type,        // "inc" | "dec" | "range"
    range,            // interval object
    incInterval,      // interval object
    decInterval,      // interval object

    ineqSign,         // e.g. ">=" | "<=" | ">" | "<"
    ineqThreshold,    // number
    ineqSolution,     // interval object
  } = data;

  // Zakładam, że w pliku istnieją te helpery (bo używasz ich w innych miejscach):
  // - formatLinear(a,b) -> latex typu "x+5" lub "-x+3"
  // - intervalObjectToLatex(intervalObj) -> latex przedziału np. "(-5,4]"
  // - ineqSignToLatex(sign) -> latex znaku nierówności np. "\\ge"
  const piece1 = formatLinear(a1, b1);
  const piece2 = formatLinear(a2, b2);

  const intervalToCasesLatex = (intv) => {
    const L = intv.leftClosed ? "\\left[" : "\\left(";
    const R = intv.rightClosed ? "\\right]" : "\\right)";
    return `${L}${intv.left},\\, ${intv.right}${R}`;
  };

  const interval1Latex = intervalToCasesLatex(interval1);
  const interval2Latex = intervalToCasesLatex(interval2);

  const funcLatex = String.raw`f(x)=
\begin{cases}
${piece1} & \text{dla } x\in ${interval1Latex},\\[4pt]
${piece2} & \text{dla } x\in ${interval2Latex}.
\end{cases}`;

  // ---------- pomocnicze: przynależność do przedziału ----------
  const inInterval = (x, intv) => {
    const leftOk = intv.leftClosed ? x >= intv.left : x > intv.left;
    const rightOk = intv.rightClosed ? x <= intv.right : x < intv.right;
    return leftOk && rightOk;
  };

  // ---------- pomocnicze: wartość funkcji ----------
  const fAt = (x) => {
    if (inInterval(x, interval1)) return a1 * x + b1;
    return a2 * x + b2; // zakładamy, że x jest z dziedziny zadania
  };

  // ===================== KROK 1: MIEJSCE ZEROWE =====================
  const x0 = zeroX;
  const zeroOnFirst = inInterval(x0, interval1);
  const aZ = zeroOnFirst ? a1 : a2;
  const bZ = zeroOnFirst ? b1 : b2;
  const pieceZLatex = zeroOnFirst ? piece1 : piece2;
  const intervalZLatex = zeroOnFirst ? interval1Latex : interval2Latex;

  const zeroSolve1 = String.raw`0 = f(x_0) = ${pieceZLatex}`;
  const zeroSolve2 = String.raw`${formatLinear(aZ, bZ)} = 0`;
  const zeroSolve3 = String.raw`x_0 = ${x0}`;

  // ===================== KROK 2: WARTOŚĆ WYRAŻENIA =====================
  const fx1 = fAt(exprX1);
  const fx2 = fAt(exprX2);

  const expr1OnFirst = inInterval(exprX1, interval1);
  const expr2OnFirst = inInterval(exprX2, interval1);

  const substitutedLinear = (a, b, x) => `${a}\\cdot(${x})${b < 0 ? "-" : "+"}${Math.abs(b)}`;
  const pieceX1 = expr1OnFirst ? substitutedLinear(a1, b1, exprX1) : substitutedLinear(a2, b2, exprX1);
  const pieceX2 = expr2OnFirst ? substitutedLinear(a1, b1, exprX2) : substitutedLinear(a2, b2, exprX2);

  const exprCalc1 = String.raw`f(${exprX1}) = ${pieceX1} = ${fx1}`;
  const exprCalc2 = String.raw`f(${exprX2}) = ${pieceX2} = ${fx2}`;
  const exprCalc3 = String.raw`f(${exprX1}) + ${k}\,f(${exprX2}) = ${fx1} + ${k}\cdot ${fx2} = ${exprValue}`;

  // ===================== KROK 3: SLOT 3 (MONOTONICZNOŚĆ / ZBIÓR WARTOŚCI) =====================
  const slot3Target =
    slot3Type === "inc"
      ? incInterval
      : slot3Type === "dec"
      ? decInterval
      : range;

  const slot3Latex = slot3Target ? intervalObjectToLatex(slot3Target) : "\\,";

  const slot3Text =
    slot3Type === "inc"
      ? "Funkcja rośnie tam, gdzie wykres idzie w górę."
      : slot3Type === "dec"
      ? "Funkcja maleje tam, gdzie wykres idzie w dół."
      : "Zbiór wartości to wszystkie wartości, jakie przyjmuje funkcja na swojej dziedzinie.";

  // POPRAWIONE: najpierw tekst zwykły, potem BlockMath
  const monoHintText =
    <>Na odcinku liniowym o monotoniczności decyduje znak współczynnika kierunkowego <InlineMath math="a" /> (nachylenie prostej).</>;
  const monoHintMath = String.raw`a>0 \Rightarrow \text{funkcja rośnie},\qquad a<0 \Rightarrow \text{funkcja maleje}.`;

  // ===================== KROK 4: NIERÓWNOŚĆ =====================
  const ineqLatex = String.raw`f(x)\ ${ineqSignToLatex(ineqSign)}\ ${ineqThreshold}`;
  const ineqSolLatex = intervalObjectToLatex(ineqSolution);

  return (
    <div className="flex flex-col gap-3" style={{ textAlign: "left" }}>
      <p>Funkcja jest określona wzorem odcinkowym:</p>
      <BlockMath math={funcLatex} />

      {/* ===================== KROK 1 ===================== */}
      <p>
        <strong>Krok 1.</strong> Szukamy miejsca zerowego, czyli takiego{" "}
        <InlineMath math={'x_0'} />, dla którego <InlineMath math={'f(x_0)=0'} />.
        W tym zadaniu <InlineMath math={'x_0'} /> leży w przedziale{" "}
        <InlineMath math={intervalZLatex} />, więc korzystamy z odpowiedniej gałęzi:
      </p>
      <BlockMath math={zeroSolve1} />
      <BlockMath math={zeroSolve2} />
      <BlockMath math={zeroSolve3} />

      {/* ===================== KROK 2 ===================== */}
      <p>
        <strong>Krok 2.</strong> Liczymy wartość wyrażenia{" "}
        <InlineMath math={`f(${exprX1})+${k}f(${exprX2})`} />.
        Najpierw wyznaczamy wartości funkcji w punktach z treści (z właściwych gałęzi):
      </p>
      <BlockMath math={exprCalc1} />
      <BlockMath math={exprCalc2} />
      <p>Następnie podstawiamy do wyrażenia:</p>
      <BlockMath math={exprCalc3} />

      {/* ===================== KROK 3 ===================== */}
      <p>
        <strong>Krok 3.</strong> {slot3Text}
      </p>

      {(slot3Type === "inc" || slot3Type === "dec") && (
        <>
          <p>{monoHintText}</p>
          <BlockMath math={monoHintMath} />
          <p>Z tego wynika, że szukana odpowiedź to przedział:</p>
        </>
      )}

      {slot3Type === "range" && (
        <p>
          Odczytujemy z wykresu najniższą i najwyższą wartość{" "}
          <InlineMath math={'y'} /> osiąganą na dziedzinie funkcji.
        </p>
      )}

      <BlockMath math={slot3Latex} />

      {/* ===================== KROK 4 ===================== */}
      <p>
        <strong>Krok 4.</strong> Rozwiązujemy nierówność{" "}
        <InlineMath math={ineqLatex} />.
        Rysujemy prostą <InlineMath math={`y=${ineqThreshold}`} /> i szukamy
        tych argumentów <InlineMath math={'x'} />, dla których wykres leży{" "}
        {ineqSign === ">" || ineqSign === ">=" ? "nad" : "pod"} tą prostą.
      </p>
      <MaturaPiecewiseInequalityExplanationGraph
        points={points}
        segments={[
          { p1: "AL", p2: "B" },
          { p1: "B", p2: "CR" },
        ]}
        solutionIntervals={ineqSolution}
        threshold={ineqThreshold}
        operator={ineqSign}
      />
      <p>Z tego otrzymujemy przedział rozwiązań:</p>
      <BlockMath math={ineqSolLatex} />
    </div>
  );
}

/* ========================================================================== */
/* PODGLĄD ODPOWIEDZI UŻYTKOWNIKA                                             */
/* ========================================================================== */

export function renderUserAnswer(userInput = {}, task) {
  const x0Raw = getFractionRaw(userInput.x0);
  const Eraw = getFractionRaw(userInput.E);
  const Rraw = getIntervalRaw(userInput, "R");
  const Sraw = getIntervalRaw(userInput, "S");

  // ----- helper: liczba (ułamek) bez etykiety -----
  const renderBareNumber = (rawVal) => {
    const raw =
      typeof rawVal === "object" ? rawVal?.raw ?? "" : (rawVal ?? "");
    const parsed = parseNumberFromAny(rawVal);

    if (!raw || raw.trim() === "") {
      return <i>(brak odpowiedzi)</i>;
    }

    if (!Number.isFinite(parsed)) {
      return (
        <>
          <span>{raw}</span> <i>(niepoprawny format)</i>
        </>
      );
    }

    const pretty = formatFracPreview(raw, "") || raw;
    return <InlineMath math={pretty} />;
  };

  // ----- helper: x0 = liczba -----
  const renderX0 = (rawVal) => {
    const raw =
      typeof rawVal === "object" ? rawVal?.raw ?? "" : (rawVal ?? "");
    const parsed = parseNumberFromAny(rawVal);

    if (!raw || raw.trim() === "") {
      return (
        <>
          <InlineMath math={"x_0 ="} /> <i>(brak odpowiedzi)</i>
        </>
      );
    }

    if (!Number.isFinite(parsed)) {
      return (
        <>
          <InlineMath math={"x_0 ="} /> <span>{raw}</span>{" "}
          <i>(niepoprawny format)</i>
        </>
      );
    }

    const pretty = formatFracPreview(raw, "") || raw;
    return <InlineMath math={`x_0 = ${pretty}`} />;
  };

  // ----- helper: sam przedział (bez tekstu) -----
  const renderBareInterval = (rawVal) => {
    const raw =
      typeof rawVal === "object" ? rawVal?.raw ?? "" : (rawVal ?? "");
    const parsed = raw && raw.trim() ? parseSingleInterval(raw) : null;

    if (!raw || raw.trim() === "") {
      return <i>(brak odpowiedzi)</i>;
    }

    if (!parsed) {
      return (
        <>
          <span>{raw}</span> <i>(niepoprawny format)</i>
        </>
      );
    }

    const intervalLatex = intervalObjectToLatex(parsed);
    return <InlineMath math={intervalLatex} />;
  };

  return (
    <div className="flex flex-col gap-1" style={{ textAlign: "left" }}>
      {/* 1. x0 = ... */}
      <div>
        <strong>1.</strong>{" "}
        {renderX0(x0Raw)}
      </div>

      {/* 2. tylko liczba */}
      <div>
        <strong>2.</strong>{" "}
        {renderBareNumber(Eraw)}
      </div>

      {/* 3. tylko przedział (monotoniczność / zbiór wartości) */}
      <div>
        <strong>3.</strong>{" "}
        {renderBareInterval(Rraw)}
      </div>

      {/* 4. tylko przedział (rozwiązanie nierówności) */}
      <div>
        <strong>4.</strong>{" "}
        {renderBareInterval(Sraw)}
      </div>
    </div>
  );
}
